'use client';

import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  RadioGroup,
  RadioGroupItem,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckCircle,
  CheckIcon,
  ChevronsUpDown,
  ExternalLink,
  Loader2,
  X,
} from 'lucide-react';
import { forwardRef, useMemo, useState } from 'react';
import { Control, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import Breadcrumbs from '@Components/Breadcrumbs';
import ImageUploadForm from '@Components/ImageUploadForm';
import PhonePreviewContainer from '@Components/PhonePreviewContainer';
import { Skeleton } from '@Components/Skeleton';
import EventPreview from '@Components/TicketedEvents/EventPreview';
import EventsSitePreview from '@Components/TicketedEvents/EventsSitePreview';
import { useEventPreviewStore } from '../../../stores/eventPreviewStore';
import Tiptap from '@Components/TipTap';
import { useAssets } from '@Hooks/useAssets';
import { useVenue } from '@Hooks/useVenue';
import service from '@Service';
import { TicketedEventTag } from '@Types/ticketedEvents';
import { cn } from '@Utils/cn';
import {
  dateTimeLocalToZoneISO,
  toISOorNull,
  toTrimmedOrNull,
} from '@Utils/utils';

type Venue = {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
};

type VenuesComboboxInputProps = {
  value?: string; // selected venueId (''/undefined for none)
  onChange: (venueId: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
  // Optional override for how the selected label is rendered
  formatLabel?: (venue: Venue) => string;
  // Optional: surface a "Create new" affordance when no results
  onCreateNewVenue?: () => void;
};

export const VenuesComboboxInput = forwardRef<
  HTMLButtonElement,
  VenuesComboboxInputProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder = 'Select a venue...',
      disabled,
      className,
      allowClear = true,
      formatLabel,
      onCreateNewVenue,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    const {
      data: venues,
      isLoading,
      isError,
      refetch,
    } = useQuery<Venue[]>({
      queryKey: ['venues'],
      queryFn: async () =>
        service.getVenues({
          'filter[disabledAt]': 'null',
        }),
    });

    const byId = useMemo(() => {
      const m = new Map<string, Venue>();
      venues?.forEach((v) => m.set(v.id, v));
      return m;
    }, [venues]);

    const selected = value ? byId.get(String(value)) : undefined;

    const defaultFormat = (v: Venue) =>
      v.city && v.state ? `${v.name} — ${v.city}, ${v.state}` : v.name;

    const selectedLabel = selected
      ? formatLabel
        ? formatLabel(selected)
        : defaultFormat(selected)
      : '';

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between',
              !selected && 'text-muted-foreground',
              className,
            )}
            disabled={disabled || isLoading || isError}
            onBlur={onBlur}
          >
            <span className="truncate">
              {isLoading ? 'Loading venues…' : selectedLabel || placeholder}
            </span>
            <span className="inline-flex items-center gap-1">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="max-w-full p-0">
          <Command shouldFilter>
            <CommandInput placeholder="Search venues…" />
            <CommandList>
              {isError ? (
                <div className="p-3 text-sm">
                  Failed to load venues.
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-2 px-0"
                    onClick={() => refetch()}
                  >
                    Retry
                  </Button>
                </div>
              ) : null}

              <CommandEmpty>
                No venues found.
                {onCreateNewVenue ? (
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-2 px-0"
                    onClick={() => {
                      setOpen(false);
                      onCreateNewVenue?.();
                    }}
                  >
                    Create new
                  </Button>
                ) : null}
              </CommandEmpty>

              <CommandGroup>
                {allowClear && value ? (
                  <CommandItem
                    value="__clear__"
                    className="text-muted-foreground"
                    onSelect={() => {
                      onChange('');
                      setOpen(false);
                    }}
                  >
                    Clear selection
                  </CommandItem>
                ) : null}

                {(venues ?? []).map((venue) => {
                  const label = formatLabel
                    ? formatLabel(venue)
                    : defaultFormat(venue);
                  const selected = value === venue.id;
                  return (
                    <CommandItem
                      key={venue.id}
                      value={label}
                      onSelect={() => {
                        onChange(venue.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selected ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="truncate">{label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

type Props = {
  value: string[]; // array of selected tag IDs
  onChange: (next: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onCreateNewTag?: () => void;
  formatLabel?: (tag: TicketedEventTag) => string;
  maxSelected?: number;
};

export const TagsMultiSelectInput = forwardRef<HTMLButtonElement, Props>(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder = 'Select tags…',
      disabled,
      className,
      onCreateNewTag,
      formatLabel,
      maxSelected,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    const {
      data: tags,
      isLoading,
      isError,
      refetch,
    } = useQuery<TicketedEventTag[]>({
      queryKey: ['ticketed-event-tags'],
      queryFn: () => service.getTicketedEventTags(),
    });

    const byId = useMemo(() => {
      const m = new Map<string, TicketedEventTag>();
      tags?.forEach((t) => m.set(t.id, t));
      return m;
    }, [tags]);

    const selectedTags: TicketedEventTag[] = (value ?? [])
      .map((id) => byId.get(id))
      .filter(Boolean) as TicketedEventTag[];

    const fmt = (t: TicketedEventTag) =>
      formatLabel
        ? formatLabel(t)
        : t.description
          ? `${t.name} — ${t.description}`
          : t.name;

    const toggle = (id: string) => {
      const exists = value.includes(id);
      if (exists) onChange(value.filter((v) => v !== id));
      else {
        if (maxSelected && value.length >= maxSelected) return;
        onChange([...value, id]);
      }
    };

    const clearAll = () => onChange([]);

    return (
      <div className={cn('space-y-2', className)}>
        {/* Trigger */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn('w-full justify-between', className)}
              disabled={disabled || isLoading || isError}
              onBlur={onBlur}
            >
              <span className="truncate">
                {isLoading
                  ? 'Loading tags…'
                  : selectedTags.length
                    ? `${selectedTags.length} selected`
                    : placeholder}
              </span>
              <span className="inline-flex items-center gap-1">
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                <ChevronsUpDown className="h-4 w-4 opacity-50" />
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
            <Command shouldFilter>
              <CommandInput placeholder="Search tags…" />
              <CommandList>
                {isError ? (
                  <div className="p-3 text-sm">
                    Failed to load tags.
                    <Button
                      variant="link"
                      size="sm"
                      className="ml-2 px-0"
                      onClick={() => refetch()}
                    >
                      Retry
                    </Button>
                  </div>
                ) : null}

                <CommandEmpty>
                  No tags found.
                  {onCreateNewTag ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="ml-2 px-0"
                      onClick={() => {
                        setOpen(false);
                        onCreateNewTag?.();
                      }}
                    >
                      Create new
                    </Button>
                  ) : null}
                </CommandEmpty>

                <CommandGroup>
                  {selectedTags.length > 0 ? (
                    <CommandItem
                      value="__clear__"
                      className="text-muted-foreground"
                      onSelect={() => clearAll()}
                    >
                      Clear all
                    </CommandItem>
                  ) : null}

                  {(tags ?? []).map((tag) => {
                    const selected = value.includes(tag.id);
                    const label = fmt(tag);
                    return (
                      <CommandItem
                        key={tag.id}
                        value={label}
                        onSelect={() => toggle(tag.id)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="truncate">{label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Selected chips */}
        {selectedTags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs"
              >
                {t.name}
                <button
                  type="button"
                  aria-label={`Remove ${t.name}`}
                  onClick={() => toggle(t.id)}
                  className="hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);

type TicketsRepeaterProps<TFieldValues> = {
  control: Control<TFieldValues>;
  name?: string; // defaults to "ticketTypes"
};

type TicketFormItem = {
  name: string;
  description?: string;
  price: string; // keep as strings in the form
  available: string; // keep as strings in the form
};

export type TicketsRepeaterValues = {
  ticketTypes: TicketFormItem[];
};

function sanitizeMoney(value: string) {
  // allow digits and a single dot; clamp to 2 decimals
  let v = value.replace(/[^\d.]/g, '');
  const parts = v.split('.');
  if (parts.length > 2) {
    v = parts[0] + '.' + parts.slice(1).join('');
  }
  const [int, dec] = v.split('.');
  return dec !== undefined ? `${int}.${dec.slice(0, 2)}` : int;
}

export function TicketsRepeater<TFieldValues>({
  control,
  name = 'ticketTypes',
}: TicketsRepeaterProps<TFieldValues>) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: name as any,
  });

  const handleAdd = () => {
    append({ name: '', description: '', price: '', available: '' } as any);
  };

  return (
    <div className="space-y-6">
      {fields.map((field, idx) => (
        <div key={field.id} className="rounded-md border p-4">
          {/* You handle the styling; this is just structure */}
          <div className="mb-4 flex items-center justify-between">
            <div className="font-semibold">Ticket #{idx + 1}</div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => remove(idx)}
              disabled={fields.length === 1}
            >
              Remove
            </Button>
          </div>

          {/* Name (required) */}
          <FormField
            control={control}
            name={`${name}.${idx}.name` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="VIP Sushi Bar Seating"
                    value={field.value == null ? '' : String(field.value)}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description (optional) */}
          <div className="mt-4">
            <FormField
              control={control}
              name={`${name}.${idx}.description` as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-y"
                      placeholder="Enjoy a seat at the sushi bar and see the chef in action."
                      value={field.value == null ? '' : String(field.value)}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Separator className="my-4" />

          {/* Price & Available (both required) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={control}
              name={`${name}.${idx}.price` as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="decimal"
                      placeholder="50.00"
                      value={field.value == null ? '' : String(field.value)}
                      onChange={(e) =>
                        field.onChange(sanitizeMoney(e.target.value))
                      }
                      onBlur={field.onBlur}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`${name}.${idx}.available` as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel># Available</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      placeholder="10"
                      value={field.value == null ? '' : String(field.value)}
                      onChange={(e) =>
                        field.onChange(e.target.value.replace(/[^\d]/g, ''))
                      }
                      onBlur={field.onBlur}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      ))}

      <Button type="button" variant="secondary" onClick={handleAdd}>
        + Add another ticket
      </Button>
    </div>
  );
}

const TicketItemSchema = z.object({
  name: z.string().min(1, 'Ticket name is required'),
  description: z.string().min(1, 'Description is required').max(500),
  price: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid price (e.g., 50 or 50.00)'),
  available: z.string().regex(/^\d+$/, 'Enter a whole number'),
});

// --------------------------- Enums (match Prisma) ---------------------------
type EventStatus = 'DRAFT' | 'PUBLISHED';
type EventVisibility = 'PUBLIC' | 'MEMBERS_ONLY' | 'PRIVATE';

const FormSchema = z
  .object({
    name: z.string().min(2, 'Event name is required'),
    description: z.string().min(1, 'Description is required'),
    teaser: z.string().min(1, 'Teaser is required').max(200),
    startDateTime: z.string().min(1, 'Start date/time is required'),
    endDateTime: z.string().min(1, 'End date/time is required'),
    capacity: z.string().regex(/^\d+$/, 'Capacity must be a whole number'),
    visibility: z.enum(['PUBLIC', 'MEMBERS_ONLY', 'PRIVATE']),
    status: z.enum(['DRAFT', 'PUBLISHED']),
    venueId: z.string().min(1, 'Please select a venue'),
    ticketTypes: z.array(TicketItemSchema),
    tags: z.array(z.string()),
    featuredImageId: z.string().optional().nullable(),
    imageIds: z.array(z.string()),
  })
  .superRefine((val, ctx) => {
    const descStripped = (val.description ?? '').replace(/<[^>]*>/g, '').trim();
    if (descStripped.length < 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['description'],
        message: 'Description is required',
      });
    }

    const hasFeaturedImage =
      val.featuredImageId != null &&
      String(val.featuredImageId).trim().length > 0;
    if (!hasFeaturedImage) {
      ctx.addIssue({
        code: 'custom',
        path: ['featuredImageId'],
        message: 'Please select a featured image',
      });
    }

    const startRaw = val.startDateTime?.trim() ?? '';
    const endRaw = val.endDateTime?.trim() ?? '';
    if (!startRaw || !endRaw) return;
    const s = new Date(startRaw);
    const e = new Date(endRaw);
    if (Number.isNaN(s.getTime()))
      ctx.addIssue({
        code: 'custom',
        path: ['startDateTime'],
        message: 'Invalid start date/time',
      });
    if (Number.isNaN(e.getTime()))
      ctx.addIssue({
        code: 'custom',
        path: ['endDateTime'],
        message: 'Invalid end date/time',
      });
    if (!Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime()) && e <= s) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDateTime'],
        message: 'End must be after start',
      });
    }
  });

type FormValues = z.infer<typeof FormSchema>;

type CreateTicketedEventPayload = {
  event: {
    name: string;
    description: string;
    teaser?: string;
    startDateTime: string | null; // ISO
    endDateTime: string | null; // ISO
    capacity: number;
    visibility: EventVisibility;
    status: EventStatus;
  };
  venueId: string;
  featuredImageId?: string;
  ticketTypes: {
    name: string;
    description: string | null;
    price: string;
    available: number;
    sortOrder: number;
  }[];
  imageIds?: string[];
};

const toInt = (v: string, fallback = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
};

// Keep 2 decimals (you can change to 4 if you want to use Decimal(11,4))
const toDecimalString = (v: string) => {
  const raw = v.trim();
  if (!raw) return '0';
  const [i, d = ''] = raw.replace(/[^\d.]/g, '').split('.');
  const dec = d.slice(0, 2);
  return dec ? `${i || '0'}.${dec}` : i || '0';
};

type Step = 1 | 2 | 3;

export default function CreateTicketedEvent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [featuredImageSheetOpen, setFeaturedImageSheetOpen] = useState(false);
  const [gallerySheetOpen, setGallerySheetOpen] = useState(false);
  const [featuredImageDialogOpen, setFeaturedImageDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<'members-app' | 'events-site'>(
    'members-app',
  );
  const [featuredImageActiveTab, setFeaturedImageActiveTab] = useState<
    'mediaLibrary' | 'uploadImage'
  >('mediaLibrary');
  const [galleryActiveTab, setGalleryActiveTab] = useState<
    'mediaLibrary' | 'uploadImage'
  >('mediaLibrary');

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      description: '',
      teaser: '',
      startDateTime: '',
      endDateTime: '',
      capacity: '0', // keep as string for input friendliness
      visibility: 'PUBLIC',
      status: 'DRAFT',
      venueId: '',
      ticketTypes: [{ name: '', description: '', price: '', available: '' }],
      tags: [],
      featuredImageId: null,
      imageIds: [],
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    trigger,
    watch,
    setValue,
    setFocus,
    formState: { isSubmitting },
  } = form;

  // Watch media fields
  const featuredImageId = watch('featuredImageId');
  const imageIds = watch('imageIds') || [];
  const venueId = watch('venueId');

  // Load assets for media selection and preview
  const { data: assets, status: assetsStatus } = useAssets({
    enabled:
      featuredImageSheetOpen ||
      gallerySheetOpen ||
      featuredImageDialogOpen ||
      galleryDialogOpen ||
      !!featuredImageId ||
      (imageIds && imageIds.length > 0),
  });

  const { data: venue } = useVenue({
    venueId,
    toastInclude: 'toastRestaurant',
    enabled: !!venueId,
  });

  const venueTimeZone =
    venue?.toastRestaurant?.general?.timeZone ?? 'America/New_York';

  // ----------------------- Live watch & preview objects ----------------------
  // This will trigger re-renders on any field change so the <pre> stays live.
  const raw = form.watch();

  const previewEvent = useMemo(() => {
    const featuredAsset = assets?.find((a) => a.id === featuredImageId);
    const galleryAssets = assets?.filter((a) => imageIds.includes(a.id)) || [];
    const startISO =
      dateTimeLocalToZoneISO(raw.startDateTime ?? '', venueTimeZone) ??
      toISOorNull(raw.startDateTime);
    const endISO =
      dateTimeLocalToZoneISO(raw.endDateTime ?? '', venueTimeZone) ??
      toISOorNull(raw.endDateTime);

    return {
      name: (raw.name || '').trim() || 'Untitled Event',
      description: raw.description || '',
      teaser: raw.teaser || null,
      startDateTime: startISO,
      endDateTime: endISO,
      capacity: toInt(raw.capacity || '0'),
      visibility: raw.visibility as EventVisibility,
      status: raw.status as EventStatus,
      featuredImage: featuredAsset
        ? {
            id: featuredAsset.id,
            cloudinaryId: featuredAsset.cloudinaryId,
          }
        : undefined,
      images: galleryAssets.map((asset) => ({
        id: asset.id,
        cloudinaryId: asset.cloudinaryId,
      })),
      ticketTypes: (() => {
        const tickets = (raw.ticketTypes ?? [])
          .filter((t: any) => t?.name?.trim() && t?.price)
          .map((t: any, idx: number) => ({
            id: `preview-ticket-${idx}`,
            name: (t?.name || '').trim(),
            description: t?.description || null,
            price: parseFloat(toDecimalString(t?.price || '0')) || 0,
            available: toInt(t?.available || '0'),
          }));
        return tickets.length > 0
          ? tickets
          : [
              {
                id: 'preview-ticket-placeholder',
                name: 'General Admission',
                description: null,
                price: 0,
                available: 0,
              },
            ];
      })(),
    };
  }, [raw, assets, featuredImageId, imageIds, venueTimeZone]);

  const step1Fields: (keyof FormValues)[] = [
    'name',
    'description',
    'teaser',
    'startDateTime',
    'endDateTime',
    'capacity',
    'visibility',
    'status',
    'venueId',
  ];

  const validateStep = async (step: Step): Promise<boolean> => {
    let fieldsToValidate: (keyof FormValues)[] = [];

    switch (step) {
      case 1:
        fieldsToValidate = step1Fields;
        break;
      case 2:
        fieldsToValidate = ['featuredImageId'];
        break;
      case 3:
        fieldsToValidate = ['ticketTypes'];
        break;
    }

    const isValid = await trigger(fieldsToValidate as any);
    if (!isValid && fieldsToValidate.length > 0) {
      setFocus(fieldsToValidate[0] as any);
    }
    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (!isValid) return;
    if (currentStep < 3) {
      setCurrentStep((prev) => (prev + 1) as Step);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as Step);
    }
  };

  const onSubmit = async (values: FormValues) => {
    const startISO =
      dateTimeLocalToZoneISO(values.startDateTime ?? '', venueTimeZone) ??
      toISOorNull(values.startDateTime);
    const endISO =
      dateTimeLocalToZoneISO(values.endDateTime ?? '', venueTimeZone) ??
      toISOorNull(values.endDateTime);

    const trimmedTeaser = values.teaser?.trim();
    const descriptionStr =
      typeof values.description === 'string' ? values.description : '';

    const payload: CreateTicketedEventPayload = {
      event: {
        name: values.name.trim(),
        description: descriptionStr || '', // API requires a string
        ...(trimmedTeaser ? { teaser: trimmedTeaser } : {}), // Omit when empty; API does not allow empty teaser
        startDateTime: startISO,
        endDateTime: endISO,
        capacity: toInt(values.capacity),
        visibility: values.visibility as EventVisibility,
        status: values.status as EventStatus,
      },

      venueId: values.venueId,

      ...(values.featuredImageId
        ? { featuredImageId: values.featuredImageId }
        : {}), // Omit when empty so API doesn't receive null

      ticketTypes: values.ticketTypes.map((t, idx) => ({
        name: t.name.trim(),
        description: toTrimmedOrNull(t.description),
        price: toDecimalString(t.price), // send as string to preserve precision
        available: toInt(t.available),
        sortOrder: idx + 1,
      })),

      imageIds:
        values.imageIds && values.imageIds.length > 0
          ? values.imageIds
          : undefined,
    };

    try {
      const response = await service.createTicketedEvent(payload);

      if (!response.ok) {
        throw new Error('Failed to create event');
      }

      const event = await response.json();

      // Navigate to the event details page
      // Handle different possible response structures
      const eventId =
        event?.data?.id || event?.id || event?.data?.attributes?.id;

      toastSuccess('Event created successfully.');

      if (eventId) {
        navigate(`/ticketed-events/${eventId}`);
      } else {
        // Fallback: navigate to events list if ID not found
        console.warn('Event created but ID not found in response:', event);
        navigate('/ticketed-events');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      // You might want to show an error toast here
      throw error; // Re-throw to let react-hook-form handle it
    }
  };

  return (
    <div>
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'Ticketed Events', href: '/ticketed-events' },
          { name: 'Create an Event', href: `/ticketed-events/create` },
        ]}
      />
      <div className="flex">
        <div className="w-2/5">
          <div className="sticky top-0">
            <div className="my-4 flex items-end">
              <Heading>Create an Event</Heading>
            </div>

            <Form {...form}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Card className="border-zinc-200">
                  <CardContent className="space-y-8">
                    <div className="flex flex-col gap-16">
                      {/* Step 1: Basic Details */}
                      <div className="flex flex-col gap-6">
                        <div className="flex items-center gap-3">
                          <Heading
                            level="h2"
                            className="flex-1 text-2xl font-semibold"
                          >
                            Basic Details
                          </Heading>
                          {currentStep > 1 && (
                            <CheckCircle className="h-7 w-7 shrink-0 text-[#CAAB6A]" />
                          )}
                        </div>
                        {currentStep === 1 && (
                          <div className="space-y-6">
                            <FormField
                              control={control}
                              name="name"
                              render={({ field }) => (
                                <FormItem className="lg:col-span-2">
                                  <FormLabel>Event name</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="Sushi Night: Omakase Experience"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Description</FormLabel>
                                  <FormControl>
                                    <Tiptap
                                      value={field.value || ''}
                                      onChange={field.onChange}
                                      onBlur={field.onBlur}
                                      placeholder="Describe your event..."
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="teaser"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Teaser</FormLabel>
                                  <FormControl>
                                    <Textarea
                                      placeholder="Chef-guided tasting with seasonal specials."
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="venueId"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Venue</FormLabel>
                                  <FormControl>
                                    <VenuesComboboxInput
                                      value={field.value}
                                      onChange={field.onChange}
                                      onBlur={field.onBlur}
                                      placeholder="Select a venue…"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                              <FormField
                                control={control}
                                name="startDateTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Start</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="datetime-local"
                                        value={field.value}
                                        onChange={(e) =>
                                          field.onChange(e.target.value)
                                        }
                                        onBlur={field.onBlur}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={control}
                                name="endDateTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>End</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="datetime-local"
                                        value={field.value}
                                        onChange={(e) =>
                                          field.onChange(e.target.value)
                                        }
                                        onBlur={field.onBlur}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <FormField
                              control={control}
                              name="capacity"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Capacity</FormLabel>
                                  <FormControl>
                                    <Input
                                      inputMode="numeric"
                                      placeholder="0"
                                      value={field.value}
                                      onChange={(e) =>
                                        field.onChange(
                                          e.target.value.replace(/[^\d]/g, ''),
                                        )
                                      }
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="visibility"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Visibility</FormLabel>
                                  <FormControl>
                                    <RadioGroup
                                      onValueChange={field.onChange}
                                      value={field.value}
                                      className="flex flex-col gap-2"
                                    >
                                      <FormItem className="flex items-center space-y-0 space-x-2">
                                        <FormControl>
                                          <RadioGroupItem
                                            id="visibility-public"
                                            value="PUBLIC"
                                          />
                                        </FormControl>
                                        <FormLabel
                                          htmlFor="visibility-public"
                                          className="font-normal"
                                        >
                                          Public
                                        </FormLabel>
                                      </FormItem>

                                      <FormItem className="flex items-center space-y-0 space-x-2">
                                        <FormControl>
                                          <RadioGroupItem
                                            id="visibility-members"
                                            value="MEMBERS_ONLY"
                                          />
                                        </FormControl>
                                        <FormLabel
                                          htmlFor="visibility-members"
                                          className="font-normal"
                                        >
                                          Members only
                                        </FormLabel>
                                      </FormItem>

                                      <FormItem className="flex items-center space-y-0 space-x-2">
                                        <FormControl>
                                          <RadioGroupItem
                                            id="visibility-private"
                                            value="PRIVATE"
                                          />
                                        </FormControl>
                                        <FormLabel
                                          htmlFor="visibility-private"
                                          className="font-normal"
                                        >
                                          Private
                                        </FormLabel>
                                      </FormItem>
                                    </RadioGroup>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="status"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Status</FormLabel>
                                  <Select
                                    onValueChange={field.onChange}
                                    value={field.value}
                                  >
                                    <FormControl>
                                      <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select status" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="DRAFT">
                                        Draft
                                      </SelectItem>
                                      <SelectItem value="PUBLISHED">
                                        Published
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="tags"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Tags</FormLabel>
                                  <FormControl>
                                    <TagsMultiSelectInput
                                      value={field.value ?? []}
                                      onChange={field.onChange}
                                      onBlur={field.onBlur}
                                      placeholder="Select tags…"
                                    />
                                  </FormControl>
                                  <FormDescription>
                                    Select one or more tags.
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Step 2: Media */}
                    <div className="flex flex-col gap-6">
                      <div className="flex items-center gap-3">
                        <Heading
                          level="h2"
                          className="flex-1 text-2xl font-semibold"
                        >
                          Media
                        </Heading>
                        {currentStep > 2 && (
                          <CheckCircle className="h-7 w-7 shrink-0 text-[#CAAB6A]" />
                        )}
                      </div>
                      {currentStep === 2 && (
                        <div className="space-y-6">
                          {/* Featured Image Section */}
                          <div className="space-y-1.5">
                            <FormLabel>Featured image</FormLabel>
                            <FormField
                              control={control}
                              name="featuredImageId"
                              render={({ field }) => {
                                const featuredAsset = assets?.find(
                                  (a) => a.id === field.value,
                                );

                                return (
                                  <FormItem>
                                    <FormControl>
                                      <div className="flex flex-col gap-2 rounded-lg bg-zinc-100 p-4">
                                        <div className="flex h-[216px] items-center justify-center overflow-hidden rounded">
                                          {featuredAsset ? (
                                            <CloudinaryImage
                                              publicId={
                                                featuredAsset.cloudinaryId
                                              }
                                              mode="original"
                                              className="h-full w-full object-cover"
                                            />
                                          ) : (
                                            <div className="flex h-full w-full items-center justify-center bg-zinc-200 text-sm text-zinc-500">
                                              No featured image selected
                                            </div>
                                          )}
                                        </div>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="w-fit self-center border-zinc-300 bg-white"
                                          onClick={() => {
                                            setFeaturedImageSheetOpen(true);
                                          }}
                                        >
                                          Change
                                        </Button>
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                );
                              }}
                            />
                          </div>

                          {/* Gallery Images Section */}
                          <div className="space-y-1.5">
                            <FormLabel>Image/Video Gallery</FormLabel>
                            <FormField
                              control={control}
                              name="imageIds"
                              render={({ field }) => {
                                const selectedAssets = assets?.filter((a) =>
                                  (field.value || []).includes(a.id),
                                );

                                return (
                                  <FormItem>
                                    <FormControl>
                                      <div className="flex flex-col gap-2 rounded-lg bg-zinc-100 p-4">
                                        {selectedAssets &&
                                        selectedAssets.length > 0 ? (
                                          <div className="flex flex-wrap gap-4">
                                            {selectedAssets.map((asset) => (
                                              <div
                                                key={asset.id}
                                                className="h-32 w-36 overflow-hidden rounded"
                                              >
                                                <CloudinaryImage
                                                  publicId={asset.cloudinaryId}
                                                  mode="original"
                                                  className="h-full w-full object-cover"
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="flex h-32 items-center justify-center text-sm text-zinc-500">
                                            No gallery images selected
                                          </div>
                                        )}
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="w-fit self-center border-zinc-300 bg-white"
                                          onClick={() => {
                                            setGallerySheetOpen(true);
                                          }}
                                        >
                                          Manage gallery images
                                        </Button>
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                );
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Step 3: Tickets & Capacity */}
                    <div className="flex flex-col gap-6">
                      <div className="flex items-center gap-3">
                        <Heading
                          level="h2"
                          className="flex-1 text-2xl font-semibold"
                        >
                          Tickets & Capacity
                        </Heading>
                      </div>
                      {currentStep === 3 && (
                        <div className="space-y-6">
                          <TicketsRepeater
                            control={
                              control as unknown as any as import('react-hook-form').Control<TicketsRepeaterValues>
                            }
                          />
                        </div>
                      )}
                    </div>

                    {/* Navigation Buttons - only show for active step */}
                    {currentStep === 1 && (
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleNext}
                          className="bg-zinc-200 hover:bg-zinc-300"
                        >
                          Next
                        </Button>
                      </div>
                    )}

                    {currentStep === 2 && (
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleBack}
                          className="bg-zinc-200 hover:bg-zinc-300"
                        >
                          Back
                        </Button>
                        <Button
                          type="button"
                          variant="primary"
                          onClick={handleNext}
                          className="bg-[#CAAB6A] hover:bg-[#B89A5A]"
                        >
                          Next
                        </Button>
                      </div>
                    )}

                    {currentStep === 3 && (
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleBack}
                          className="bg-zinc-200 hover:bg-zinc-300"
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          loading={isSubmitting}
                          className="bg-[#CAAB6A] hover:bg-[#B89A5A]"
                        >
                          Create Event
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </form>
            </Form>
          </div>
        </div>
        <div className="w-3/5">
          {/* Dev Preview */}
          <section className="sticky top-8 space-y-4 p-4">
            <div className="flex flex-col space-y-8 xl:col-span-2">
              {/* Preview Pane */}
              <div className="sticky top-4">
                <div className="mb-6 text-center">
                  <Heading level="h3">Preview</Heading>

                  <div className="mx-auto mt-2 flex max-w-xs items-stretch gap-1 rounded-lg bg-zinc-100 p-1">
                    <Button
                      type="button"
                      variant={
                        previewMode === 'members-app' ? 'secondary' : 'ghost'
                      }
                      className={cn(
                        'flex-1 hover:bg-white',
                        previewMode === 'members-app' && 'bg-white shadow-md',
                      )}
                      onClick={() => setPreviewMode('members-app')}
                      size="sm"
                    >
                      Members App
                    </Button>
                    <Button
                      type="button"
                      variant={
                        previewMode === 'events-site' ? 'secondary' : 'ghost'
                      }
                      className={cn(
                        'flex-1 hover:bg-white',
                        previewMode === 'events-site' && 'bg-white shadow-md',
                      )}
                      onClick={() => setPreviewMode('events-site')}
                      size="sm"
                    >
                      Events Site
                    </Button>
                  </div>
                </div>
                {/* Phone Frame */}
                <PhonePreviewContainer>
                  {previewMode === 'members-app' ? (
                    <EventPreview
                      event={previewEvent as any}
                      venue={venue}
                    />
                  ) : (
                    <EventsSitePreview
                      event={previewEvent as any}
                      venue={venue}
                    />
                  )}
                </PhonePreviewContainer>
                {/* Preview in new tab button */}
                {previewMode === 'events-site' && (
                  <div className="z-10 mt-8 mb-2 flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        useEventPreviewStore
                          .getState()
                          .setPreviewData({
                            event: previewEvent as any,
                            venue,
                          });
                        window.open(
                          '/ticketed-events/preview',
                          'event-preview',
                        );
                      }}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Preview in new tab
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Featured Image Selection Sheet */}
      <Sheet
        open={featuredImageSheetOpen}
        onOpenChange={setFeaturedImageSheetOpen}
      >
        <SheetContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle>Select featured image</SheetTitle>
            </SheetHeader>

            <SheetBody className="grow overflow-auto">
              <div className="space-y-6">
                {/* Current Featured Image Preview */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-medium">Featured Image</div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFeaturedImageDialogOpen(true)}
                    >
                      {featuredImageId ? 'Change' : 'Select'} featured image
                    </Button>
                  </div>
                  {featuredImageId ? (
                    (() => {
                      const featuredAsset = assets?.find(
                        (a) => a.id === featuredImageId,
                      );
                      return featuredAsset ? (
                        <div className="flex min-h-42 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                          <CloudinaryImage
                            publicId={featuredAsset.cloudinaryId}
                            mode="original"
                            className="h-64 w-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                          <div className="text-sm text-zinc-500">
                            Loading image...
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                      <div className="text-sm text-zinc-500">
                        No featured image selected
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Dialog
                open={featuredImageDialogOpen}
                onOpenChange={setFeaturedImageDialogOpen}
              >
                <DialogContent className="sm:max-w-xl">
                  <DialogHeader>
                    <DialogTitle>Select featured image</DialogTitle>
                  </DialogHeader>

                  <Tabs
                    value={featuredImageActiveTab}
                    onValueChange={(val) =>
                      setFeaturedImageActiveTab(
                        val as 'mediaLibrary' | 'uploadImage',
                      )
                    }
                    className="w-full"
                  >
                    <TabsList>
                      <TabsTrigger value="mediaLibrary">
                        Media Library
                      </TabsTrigger>
                      <TabsTrigger value="uploadImage">Upload</TabsTrigger>
                    </TabsList>

                    {/* Media Library Tab */}
                    <TabsContent value="mediaLibrary" className="mt-4">
                      <div className="grid max-h-84 grid-cols-3 gap-4 overflow-y-auto p-1">
                        {assetsStatus === 'pending' ? (
                          <>
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="aspect-square w-full" />
                          </>
                        ) : !assets || assets.length === 0 ? (
                          <div className="col-span-3 flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-100 text-center text-sm text-zinc-500">
                            <div className="font-medium text-zinc-700">
                              No uploaded images
                            </div>
                          </div>
                        ) : (
                          assets.map((asset) => {
                            const selected = featuredImageId === asset.id;
                            return (
                              <button
                                key={asset.id}
                                type="button"
                                onClick={() => {
                                  setValue(
                                    'featuredImageId',
                                    selected ? null : asset.id,
                                  );
                                  setFeaturedImageDialogOpen(false);
                                }}
                                className={cn(
                                  'relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border bg-zinc-200 transition',
                                  selected
                                    ? 'border-primary ring-4 ring-[#CAAB6A]'
                                    : 'border-transparent hover:border-zinc-300',
                                )}
                              >
                                <CloudinaryImage
                                  publicId={asset.cloudinaryId}
                                  mode="original"
                                  className="max-h-full max-w-full object-contain"
                                />
                                {selected && (
                                  <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#CAAB6A] bg-black">
                                    <CheckIcon className="h-4 w-4 text-[#CAAB6A]" />
                                  </div>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </TabsContent>

                    {/* Upload Tab */}
                    <TabsContent value="uploadImage" className="mt-4">
                      <ImageUploadForm
                        inputId="featured-image-upload-input"
                        onUploadSuccess={() => {
                          queryClient.invalidateQueries({
                            queryKey: ['assets'],
                          });
                        }}
                      />
                    </TabsContent>
                  </Tabs>

                  {featuredImageActiveTab === 'mediaLibrary' && (
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="link">
                          Cancel
                        </Button>
                      </DialogClose>
                    </DialogFooter>
                  )}
                </DialogContent>
              </Dialog>
            </SheetBody>

            <SheetFooter>
              <Button
                variant="secondary"
                onClick={() => setFeaturedImageSheetOpen(false)}
              >
                Done
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>

      {/* Gallery Images Selection Sheet */}
      <Sheet open={gallerySheetOpen} onOpenChange={setGallerySheetOpen}>
        <SheetContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle>Manage gallery images</SheetTitle>
            </SheetHeader>

            <SheetBody className="grow overflow-auto">
              <div className="space-y-6">
                {/* Current Gallery Images Preview */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-medium">Gallery Images</div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGalleryDialogOpen(true)}
                    >
                      Manage images
                    </Button>
                  </div>
                  {imageIds && imageIds.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4">
                      {imageIds.map((imageId) => {
                        const asset = assets?.find((a) => a.id === imageId);
                        return (
                          <div
                            key={imageId}
                            className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100"
                          >
                            {asset ? (
                              <CloudinaryImage
                                publicId={asset.cloudinaryId}
                                mode="original"
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-xs text-zinc-500">
                                Loading...
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 py-8 text-center">
                      <div className="text-sm text-zinc-500">
                        No gallery images selected
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Dialog
                open={galleryDialogOpen}
                onOpenChange={setGalleryDialogOpen}
              >
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
                  <DialogHeader>
                    <DialogTitle>Select gallery images</DialogTitle>
                  </DialogHeader>

                  <Tabs
                    value={galleryActiveTab}
                    onValueChange={(val) =>
                      setGalleryActiveTab(val as 'mediaLibrary' | 'uploadImage')
                    }
                    className="w-full"
                  >
                    <TabsList>
                      <TabsTrigger value="mediaLibrary">
                        Media Library
                      </TabsTrigger>
                      <TabsTrigger value="uploadImage">Upload</TabsTrigger>
                    </TabsList>

                    {/* Media Library Tab */}
                    <TabsContent value="mediaLibrary" className="mt-4">
                      <div className="grid max-h-84 grid-cols-3 gap-4 overflow-y-auto p-1">
                        {assetsStatus === 'pending' ? (
                          <>
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="aspect-square w-full" />
                          </>
                        ) : !assets || assets.length === 0 ? (
                          <div className="col-span-3 flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-100 text-center text-sm text-zinc-500">
                            <div className="font-medium text-zinc-700">
                              No uploaded images
                            </div>
                          </div>
                        ) : (
                          assets.map((asset) => {
                            const selected = imageIds.includes(asset.id);
                            return (
                              <button
                                key={asset.id}
                                type="button"
                                onClick={() => {
                                  const currentIds = imageIds || [];
                                  const newIds = selected
                                    ? currentIds.filter((id) => id !== asset.id)
                                    : [...currentIds, asset.id];
                                  setValue('imageIds', newIds);
                                }}
                                className={cn(
                                  'relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border bg-zinc-200 transition',
                                  selected
                                    ? 'border-primary ring-4 ring-[#CAAB6A]'
                                    : 'border-transparent hover:border-zinc-300',
                                )}
                              >
                                <CloudinaryImage
                                  publicId={asset.cloudinaryId}
                                  mode="original"
                                  className="max-h-full max-w-full object-contain"
                                />
                                {selected && (
                                  <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#CAAB6A] bg-black">
                                    <CheckIcon className="h-4 w-4 text-[#CAAB6A]" />
                                  </div>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </TabsContent>

                    {/* Upload Tab */}
                    <TabsContent value="uploadImage" className="mt-4">
                      <ImageUploadForm
                        inputId="gallery-image-upload-input"
                        onUploadSuccess={() => {
                          queryClient.invalidateQueries({
                            queryKey: ['assets'],
                          });
                        }}
                      />
                    </TabsContent>
                  </Tabs>

                  {galleryActiveTab === 'mediaLibrary' && (
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="link">
                          Cancel
                        </Button>
                      </DialogClose>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => {
                          setGalleryDialogOpen(false);
                        }}
                      >
                        Save
                      </Button>
                    </DialogFooter>
                  )}
                </DialogContent>
              </Dialog>
            </SheetBody>

            <SheetFooter>
              <Button
                variant="secondary"
                onClick={() => setGallerySheetOpen(false)}
              >
                Done
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
